import { useLocalStorage } from "@elit-hair/elit-tools";
import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { VscTriangleRight } from "react-icons/vsc";

import { RESIZE_DEBOUNCE_MS, debounce } from "@/utils/debounce";
import { normalizeCssValue } from "@/utils/normalize-css-value";

// Default fallback values for sizing constraints
const DEFAULT_MIN_WIDTH = 10;
const DEFAULT_MAX_WIDTH = typeof window !== "undefined" ? window.innerWidth - 100 : 1200;
const DEFAULT_MIN_HEIGHT = 10;
const DEFAULT_MAX_HEIGHT = typeof window !== "undefined" ? window.innerHeight - 100 : 800;

// Simple triangle icon component
const TriangleRight: React.FC<{ size?: number; resizeIndicatorColor?: string }> = ({
    size = 16,
    resizeIndicatorColor,
}) => <VscTriangleRight size={size} color={resizeIndicatorColor} className="text-primary" />;

type DraggableWrapperProps = {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    initialPosition?: { x: number; y: number };
    bounds?: {
        minX?: number;
        maxX?: number;
        minY?: number;
        maxY?: number;
    };
    onDragStart?: () => void;
    onDragEnd?: (position: { x: number; y: number }) => void;
    onDrag?: (position: { x: number; y: number }) => void;
    disabled?: boolean;
    dragHandle?: string;
    zIndex?: number;
    wrappedComponentName?: string;

    // Sizing props
    width?: number | string;
    height?: number | string;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    isResizable?: boolean;

    // Styling props
    border?: boolean | string;
    borderRadius?: number | string;
    backgroundColor?: string;
    shadow?: boolean | string;
    buttonColor?: string;
    backdropBlur?: number;
    resizeIndicatorColor?: string;

    // Content structure props
    header?: ReactNode;
    footer?: ReactNode;
    scrollableContent?: boolean;

    // Persistence props
    storageKey?: string;
    persistSize?: boolean;
    persistPosition?: boolean;

    // Event handlers
    onResizeStart?: () => void;
    onResizeEnd?: (size: { width: number; height: number }) => void;
    onResize?: (size: { width: number; height: number }) => void;
    onClose?: () => void;

    // Resize indicator props
    resizeIndicator?: {
        icon?: ReactNode;
        color?: string;
        size?: number;
        rotation?: number;
        opacity?: number;
        position: "bottom-right" | "bottom-left" | "top-right" | "top-left";
    };
};

// Ensure position stays within viewport bounds
const constrainToViewport = (
    position: { x: number; y: number },
    elementWidth: number,
    elementHeight: number
): { x: number; y: number } => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return {
        x: Math.max(0, Math.min(position.x, viewportWidth - elementWidth)),
        y: Math.max(0, Math.min(position.y, viewportHeight - elementHeight)),
    };
};

// Constrain size within limits
const constrainSize = (
    size: { width: number; height: number },
    minWidth: number,
    maxWidth: number,
    minHeight: number,
    maxHeight: number
): { width: number; height: number } => {
    return {
        width: Math.max(minWidth, Math.min(size.width, maxWidth)),
        height: Math.max(minHeight, Math.min(size.height, maxHeight)),
    };
};

export const DraggableWrapper: React.FC<DraggableWrapperProps> = ({
    children,
    className = "",
    style = {},
    initialPosition = { x: 0, y: 0 },
    bounds,
    onDragStart,
    onDragEnd,
    onDrag,
    disabled = false,
    dragHandle,
    zIndex = 1000,
    wrappedComponentName,

    // Sizing props
    width = "auto",
    height = "auto",
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    isResizable = false,

    // Styling props
    border = true,
    borderRadius = 8,
    backgroundColor,
    shadow = true,
    buttonColor,
    backdropBlur = 10,
    resizeIndicatorColor,

    // Content structure props
    header,
    footer,
    scrollableContent = false,

    // Persistence props
    storageKey,
    persistSize = false,
    persistPosition = false,

    // Event handlers
    onResizeStart,
    onResizeEnd,
    onResize,
    onClose,

    // Resize indicator props
    resizeIndicator,
}) => {
    const [storedPosition, setStoredPosition] =
        persistPosition && storageKey ? useLocalStorage(`${storageKey}_position`, initialPosition) : [null, null];

    const [storedSize, setStoredSize] =
        persistSize && storageKey
            ? useLocalStorage(`${storageKey}_size`, {
                  width: normalizeCssValue(width, 300),
                  height: normalizeCssValue(height, 200),
              })
            : [null, null];

    const [position, setPosition] = useState(storedPosition || initialPosition);
    const [size, setSize] = useState(
        storedSize || {
            width: normalizeCssValue(width, 300),
            height: normalizeCssValue(height, 200),
        }
    );

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [resizeHandle, setResizeHandle] = useState<string | null>(null);
    const [elementSize, setElementSize] = useState({ width: 0, height: 0 });
    const elementRef = useRef<HTMLDivElement>(null);
    const isInitialized = useRef(false);
    const lastViewportSize = useRef({ width: window.innerWidth, height: window.innerHeight });

    // Load stored position on mount
    useEffect(() => {
        if (storedPosition && !isInitialized.current) {
            setPosition(storedPosition);
            isInitialized.current = true;
        }
    }, [storedPosition]);

    useEffect(() => {
        if (persistPosition && setStoredPosition) {
            setStoredPosition(position);
        }
    }, [position, persistPosition, setStoredPosition]);

    useEffect(() => {
        if (persistSize && setStoredSize) {
            setStoredSize(size);
        }
    }, [size, persistSize, setStoredSize]);

    // Initialize position and handle resize
    useEffect(() => {
        const updatePosition = () => {
            if (elementRef.current) {
                const rect = elementRef.current.getBoundingClientRect();
                const newElementSize = { width: rect.width, height: rect.height };
                const currentViewportSize = { width: window.innerWidth, height: window.innerHeight };

                // Only update element size if it actually changed
                if (elementSize.width !== newElementSize.width || elementSize.height !== newElementSize.height) {
                    setElementSize(newElementSize);
                }

                // Check if viewport size changed
                const viewportChanged =
                    lastViewportSize.current.width !== currentViewportSize.width ||
                    lastViewportSize.current.height !== currentViewportSize.height;

                if (viewportChanged) {
                    lastViewportSize.current = currentViewportSize;

                    // Recalculate position to ensure it stays within new viewport bounds
                    const constrainedPosition = constrainToViewport(
                        position,
                        newElementSize.width,
                        newElementSize.height
                    );

                    if (
                        Math.abs(position.x - constrainedPosition.x) > 1 ||
                        Math.abs(position.y - constrainedPosition.y) > 1
                    ) {
                        setPosition(constrainedPosition);
                    }
                } else if (!isInitialized.current) {
                    const startPosition = storedPosition || initialPosition;
                    const constrainedPosition = constrainToViewport(
                        startPosition,
                        newElementSize.width,
                        newElementSize.height
                    );

                    if (
                        !isInitialized.current ||
                        Math.abs(position.x - constrainedPosition.x) > 1 ||
                        Math.abs(position.y - constrainedPosition.y) > 1
                    ) {
                        setPosition(constrainedPosition);
                        isInitialized.current = true;
                    }
                }
            }
        };

        // Initial setup
        updatePosition();

        // Handle window resize with debouncing
        const handleResize = debounce(updatePosition, RESIZE_DEBOUNCE_MS);

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, [initialPosition.x, initialPosition.y, elementSize.width, elementSize.height, storedPosition]);

    // Drag handlers
    useEffect(() => {
        if (disabled || isResizing) return; // Don't allow dragging while resizing

        const handleMouseDown = (e: MouseEvent) => {
            // Don't start dragging if clicking on resize handles
            if ((e.target as Element).closest(".resize-handle")) {
                return;
            }

            if (dragHandle && !(e.target as Element).closest(dragHandle)) {
                return;
            }

            e.preventDefault();
            setIsDragging(true);

            const rect = elementRef.current?.getBoundingClientRect();
            if (rect) {
                setDragOffset({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                });
            }

            onDragStart?.();
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            let boundedX = newX;
            let boundedY = newY;

            if (bounds) {
                if (bounds.minX !== undefined) boundedX = Math.max(boundedX, bounds.minX);
                if (bounds.maxX !== undefined) boundedX = Math.min(boundedX, bounds.maxX);
                if (bounds.minY !== undefined) boundedY = Math.max(boundedY, bounds.minY);
                if (bounds.maxY !== undefined) boundedY = Math.min(boundedY, bounds.maxY);
            }

            // Apply viewport constraints
            const viewportConstrained = constrainToViewport(
                { x: boundedX, y: boundedY },
                elementSize.width,
                elementSize.height
            );

            const newPosition = { x: viewportConstrained.x, y: viewportConstrained.y };
            setPosition(newPosition);
            onDrag?.(newPosition);
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                onDragEnd?.(position);
            }
        };

        const element = elementRef.current;
        if (element) {
            element.addEventListener("mousedown", handleMouseDown);
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);

            return () => {
                element.removeEventListener("mousedown", handleMouseDown);
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [
        isDragging,
        dragOffset,
        onDragStart,
        onDragEnd,
        onDrag,
        disabled,
        dragHandle,
        bounds,
        elementSize.width,
        elementSize.height,
        isResizing,
    ]);

    // Resize handlers
    useEffect(() => {
        if (!isResizable) return;

        const handleResizeMove = (e: MouseEvent) => {
            if (!isResizing || !elementRef.current) return;

            const rect = elementRef.current.getBoundingClientRect();
            let newWidth = size.width;
            let newHeight = size.height;

            if (resizeHandle?.includes("right")) {
                newWidth = e.clientX - rect.left;
            }
            if (resizeHandle?.includes("bottom")) {
                newHeight = e.clientY - rect.top;
            }

            // Constrain size
            const constrainedSize = constrainSize(
                { width: newWidth, height: newHeight },
                minWidth || DEFAULT_MIN_WIDTH,
                maxWidth || DEFAULT_MAX_WIDTH,
                minHeight || DEFAULT_MIN_HEIGHT,
                maxHeight || DEFAULT_MAX_HEIGHT
            );

            setSize(constrainedSize);
            onResize?.(constrainedSize);
        };

        const handleResizeEnd = () => {
            if (isResizing) {
                setIsResizing(false);
                setResizeHandle(null);
                onResizeEnd?.(size);
            }
        };

        const element = elementRef.current;
        if (element) {
            document.addEventListener("mousemove", handleResizeMove);
            document.addEventListener("mouseup", handleResizeEnd);

            return () => {
                document.removeEventListener("mousemove", handleResizeMove);
                document.removeEventListener("mouseup", handleResizeEnd);
            };
        }
    }, [
        isResizable,
        isResizing,
        resizeHandle,
        size,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        onResizeStart,
        onResizeEnd,
        onResize,
    ]);

    // Touch events for mobile support
    useEffect(() => {
        if (disabled || isResizing) return; // Don't allow touch dragging while resizing

        const handleTouchStart = (e: TouchEvent) => {
            if (dragHandle && !(e.target as Element).closest(dragHandle)) {
                return;
            }

            e.preventDefault();
            setIsDragging(true);

            const rect = elementRef.current?.getBoundingClientRect();
            if (rect && e.touches[0]) {
                setDragOffset({
                    x: e.touches[0].clientX - rect.left,
                    y: e.touches[0].clientY - rect.top,
                });
            }

            onDragStart?.();
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!isDragging) return;

            e.preventDefault();

            if (e.touches[0]) {
                const newX = e.touches[0].clientX - dragOffset.x;
                const newY = e.touches[0].clientY - dragOffset.y;

                let boundedX = newX;
                let boundedY = newY;

                if (bounds) {
                    if (bounds.minX !== undefined) boundedX = Math.max(boundedX, bounds.minX);
                    if (bounds.maxX !== undefined) boundedX = Math.min(boundedX, bounds.maxX);
                    if (bounds.minY !== undefined) boundedY = Math.max(boundedY, bounds.minY);
                    if (bounds.maxY !== undefined) boundedY = Math.min(boundedY, bounds.maxY);
                }

                // Apply viewport constraints
                const viewportConstrained = constrainToViewport(
                    { x: boundedX, y: boundedY },
                    elementSize.width,
                    elementSize.height
                );

                const newPosition = { x: viewportConstrained.x, y: viewportConstrained.y };
                setPosition(newPosition);
                onDrag?.(newPosition);
            }
        };

        const handleTouchEnd = () => {
            if (isDragging) {
                setIsDragging(false);
                onDragEnd?.(position);
            }
        };

        const element = elementRef.current;
        if (element) {
            element.addEventListener("touchstart", handleTouchStart, { passive: false });
            document.addEventListener("touchmove", handleTouchMove, { passive: false });
            document.addEventListener("touchend", handleTouchEnd);

            return () => {
                element.removeEventListener("touchstart", handleTouchStart);
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            };
        }
    }, [
        isDragging,
        dragOffset,
        onDragStart,
        onDragEnd,
        onDrag,
        disabled,
        dragHandle,
        bounds,
        elementSize.width,
        elementSize.height,
        isResizing,
    ]);

    // Build styles
    const containerStyle: CSSProperties = {
        position: "fixed",
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: isDragging || isResizing ? zIndex + 1 : zIndex,
        cursor: isDragging ? "grabbing" : "auto",
        userSelect: "none",
        touchAction: "none",
        backgroundColor,
        borderRadius: normalizeCssValue(borderRadius, 8),
        border: border ? (typeof border === "string" ? border : "1px solid #e5e7eb") : "none",
        backdropFilter: backdropBlur ? `blur(${backdropBlur}px)` : "none",
        boxShadow: shadow
            ? typeof shadow === "string"
                ? shadow
                : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
            : "none",
        display: "flex",
        flexDirection: "column",
        ...style,
    };

    const contentStyle: CSSProperties = {
        flex: 1,
        overflow: scrollableContent ? "auto" : "visible",
        padding: header || footer ? "16px" : "0",
    };

    const resizeHandleStyle: CSSProperties = {
        position: "absolute",
        width: "12px",
        height: "12px",
        backgroundColor: "transparent",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: "2px",
        cursor: "nw-resize",
        zIndex: 1,
        opacity: 0,
        transition: "opacity 0.2s ease",
    };

    // Resize handle click handler
    const handleResizeHandleClick = (e: React.MouseEvent, handle: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeHandle(handle);
        onResizeStart?.();
    };
    const [showResizeHandles, setShowResizeHandles] = useState(false);

    return (
        <div
            data-name={wrappedComponentName}
            ref={elementRef}
            className={`draggable-wrapper ${className}`}
            style={containerStyle}
            onMouseEnter={() => {
                setShowResizeHandles(true);
            }}
            onMouseLeave={() => {
                setShowResizeHandles(false);
            }}
        >
            {/* Header */}
            {header && (
                <div
                    className="draggable-header"
                    style={{
                        padding: "12px 16px",
                        borderBottom: border ? (typeof border === "string" ? border : "1px solid #e5e7eb") : "none",
                        backgroundColor: backgroundColor,
                        borderTopLeftRadius: normalizeCssValue(borderRadius, 8),
                        borderTopRightRadius: normalizeCssValue(borderRadius, 8),
                    }}
                >
                    {header}
                    {onClose && (
                        <button
                            onClick={onClose}
                            style={{
                                position: "absolute",
                                right: "8px",
                                top: "8px",
                                background: "none",
                                border: "none",
                                fontSize: "18px",
                                cursor: "pointer",
                                color: buttonColor || "#6b7280",
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>
            )}

            {/* Content */}
            <div style={contentStyle}>{children}</div>

            {/* Footer */}
            {footer && (
                <div
                    className="draggable-footer"
                    style={{
                        padding: "12px 16px",
                        borderTop: border ? (typeof border === "string" ? border : "1px solid #e5e7eb") : "none",
                        backgroundColor: backgroundColor,
                        borderBottomLeftRadius: normalizeCssValue(borderRadius, 8),
                        borderBottomRightRadius: normalizeCssValue(borderRadius, 8),
                    }}
                >
                    {footer}
                </div>
            )}

            {/* Resize indicator icon */}
            {isResizable && showResizeHandles && (
                <div
                    style={{
                        position: "absolute",
                        bottom: resizeIndicator?.position?.includes("bottom") ? "4px" : "auto",
                        top: resizeIndicator?.position?.includes("top") ? "4px" : "auto",
                        right: resizeIndicator?.position?.includes("right") ? "4px" : "auto",
                        left: resizeIndicator?.position?.includes("left") ? "4px" : "auto",
                        opacity: resizeIndicator?.opacity ?? 0.6,
                        pointerEvents: "none",
                        zIndex: 1,
                        transform: resizeIndicator?.rotation ? `rotate(${resizeIndicator.rotation}deg)` : "none",
                    }}
                >
                    {resizeIndicator?.icon || (
                        <TriangleRight
                            size={resizeIndicator?.size ?? 20}
                            resizeIndicatorColor={resizeIndicator?.color ?? resizeIndicatorColor ?? "gray"}
                        />
                    )}
                </div>
            )}

            {/* Resize handles */}
            {isResizable && showResizeHandles && (
                <>
                    <div
                        className="resize-handle"
                        style={{
                            ...resizeHandleStyle,
                            right: "2px",
                            bottom: "2px",
                            cursor: "nw-resize",
                        }}
                        onMouseDown={(e) => handleResizeHandleClick(e, "right-bottom")}
                    />
                    <div
                        className="resize-handle"
                        style={{
                            ...resizeHandleStyle,
                            right: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "ew-resize",
                            width: "6px",
                            height: "20px",
                            borderRadius: "3px",
                        }}
                        onMouseDown={(e) => handleResizeHandleClick(e, "right")}
                    />
                    <div
                        className="resize-handle"
                        style={{
                            ...resizeHandleStyle,
                            left: "50%",
                            bottom: "2px",
                            transform: "translateX(-50%)",
                            cursor: "ns-resize",
                            width: "20px",
                            height: "6px",
                            borderRadius: "3px",
                        }}
                        onMouseDown={(e) => handleResizeHandleClick(e, "bottom")}
                    />
                </>
            )}
        </div>
    );
};
